import patient from '@/sui/data/sheets/edit/patient'
import catheter from '@/sui/data/sheets/edit/catheter'
import bact from '@/sui/data/sheets/edit/bact'
import bounds from '@/sui/data/sheets/edit/bounds'
import notes from '@/sui/data/sheets/edit/notes'
import fields from '@/sui/data/sheets/edit/fields'
import assert from '@/sui/tools/assert'
import cfg from '@/sui/data/sheets/edit/cfg/neoInfADI'

import common from '@/sui/data/sheets/edit/notes/common'

function checkBacteremias (result, sheet) {
  const bacts = sheet[fields.bactArray]
  const bactsNotes = result[fields.bactArray]
  assert.equals(bacts.length, bactsNotes.length)

  for (let i in bacts) {
    const bact = bacts[i]
    const notes = bactsNotes[i]
    common.checkBactMicroOrg(notes, bact, fields.bacteremia)
  }
}

function getMandatoryCatheters (year) {
  if (year === 2019) {
    return [1, 2]
  }

  if (year <= 2025) {
    return ['C1', 'C3']
  }

  return ['C1', 'C3', 'C4']
}

function checkKts (result, sheet, year) {
  const dead = sheet[fields.dead]
  const kts = sheet[fields.catheterArray]
  const ktsNotes = result[fields.catheterArray]
  assert.equals(kts.length, ktsNotes.length)

  const removalMessage = `Incohérence entre le champ «Retrait du cathéter»
et le champ «Envoi au laboratoire»`
  const deadMessage = `Incohérence entre le motif du retrait
et le champ «Décès»`
  let found = false
  const mandatory = getMandatoryCatheters(year)

  for (let i in kts) {
    const kt = kts[i]
    const ktType = kt[fields.catheterType]
    const notes = ktsNotes[i]
    if (mandatory.indexOf(ktType) !== -1) {
      found = true
    }

    if (dead != null && dead !== 1) {
      const reason = kt[fields.removalReason]
      if (reason === 5) {
        const deadNote = result[fields.dead]
        const note = notes[fields.removalReason]
        deadNote.type = note.type = 'error'
        deadNote.tooltip = note.tooltip = deadMessage
      }
    }

    const labo = kt[fields.laboratory]
    const removed = kt[fields.removed]
    if (removed && labo === 3) {
      const laboNote = notes[fields.laboratory]
      const removedNote = notes[fields.removed]
      laboNote.type = removedNote.type = 'error'
      laboNote.tooltip = removedNote.tooltip = removalMessage
    }
    const iscol = kt[fields.isColIlcBlcSep]
    if (iscol != null && iscol >= 2 && iscol <= 4) {
      common.checkMicroOrg(notes, kt, fields.isColIlcBlcSep, 'Une infection')
    }
  }

  if (!found) {
    const type = sheet[fields.unitType]
    if (type != null) {
      const note = result[fields.unitType]
      note.type = 'error'
      note.tooltip = `Une fiche doit inclure au moins un cathéter
central, ombilical ou un PICC`
    }
  }
}

function checkNumericValues (result, sheet) {
  const numericChecks = [
    bounds.cribII,
    bounds.birthWeight,
    bounds.gestationalAge
  ]
  notes.checkNumerics(result, sheet, numericChecks)
}

function checkDates (result, sheet, year) {
  const v2 = year !== 2019
  const oldest = notes.neoOldest[year]
  const newest = notes.newest[year]
  const start = notes.start[year]
  const birthdate = notes.dateField(result, sheet, patient.birthdate)
  const entrydate = notes.dateField(result, sheet, patient.entrydate)
  const deathdate = notes.dateField(result, sheet, patient.deathDate)
  const exitdate = notes.dateField(result, sheet, patient.exitDate)

  const comparisons = [
    { f: oldest, s: birthdate },

    { f: birthdate, s: newest },
    { f: entrydate, s: newest },
    { f: exitdate, s: newest },
    { f: deathdate, s: newest },

    { f: birthdate, s: entrydate },
    { f: birthdate, s: exitdate },
    { f: birthdate, s: deathdate },

    { f: entrydate, s: deathdate },
    { f: entrydate, s: exitdate }
  ]

  const kts = sheet[fields.catheterArray]
  const ktsNotes = result[fields.catheterArray]
  assert.equals(kts.length, ktsNotes.length)

  for (let i in kts) {
    const kt = kts[i]
    const note = ktsNotes[i]
    const insertiondate = notes.dateField(note, kt, catheter.insertionDate)
    const removaldate = notes.dateField(note, kt, catheter.removalDate)
    const coldate = notes.dateField(note, kt, cfg.colIlcBlcSep)
    if (year >= 2025) {
      common.checkEpisodAndEntry(coldate, entrydate)
    }

    comparisons.push(
      { f: start, s: coldate },
      { f: insertiondate, s: newest },
      { f: removaldate, s: newest },
      { f: coldate, s: newest },

      { f: birthdate, s: insertiondate },
      { f: insertiondate, s: removaldate },
      { f: insertiondate, s: coldate },
      { f: insertiondate, s: deathdate },
      { f: insertiondate, s: exitdate },

      { f: birthdate, s: removaldate },
      { f: entrydate, s: removaldate },
      { f: removaldate, s: deathdate },
      { f: removaldate, s: exitdate },

      { f: birthdate, s: coldate },
      { f: coldate, s: deathdate },
      { f: coldate, s: exitdate }
    )

    if (v2) {
      const diagdate = notes.dateField(note, kt, bact.diagDate.v1)
      comparisons.push(
        { f: oldest, s: diagdate },
        { f: birthdate, s: diagdate },
        { f: coldate, s: diagdate }
      )
    }
  }

  const bacts = sheet[fields.bactArray]
  const bactsNotes = result[fields.bactArray]
  assert.equals(bacts.length, bactsNotes.length)

  for (let i in bacts) {
    const bactf = bacts[i]
    const note = bactsNotes[i]

    const episoddate = notes.dateField(note, bactf, bact.episod)

    if (year >= 2025) {
      common.checkEpisodAndEntry(episoddate, entrydate)
    }

    const insertiondate = notes.dateField(note, bactf, bact.insertionDate.v1)
    const insertiondate2 = notes.dateField(
      note, bactf, bact.enfInsertionDate.v1
    )
    const removaldate = notes.dateField(note, bactf, bact.removalDate.v1)
    comparisons.push(
      { f: start, s: episoddate },
      { f: oldest, s: removaldate },

      { f: episoddate, s: newest },
      { f: insertiondate, s: newest },
      { f: insertiondate2, s: newest },
      { f: removaldate, s: newest },

      { f: birthdate, s: episoddate },
      { f: episoddate, s: deathdate },
      { f: episoddate, s: exitdate },

      { f: birthdate, s: insertiondate },
      { f: insertiondate, s: episoddate },
      { f: insertiondate, s: removaldate },
      { f: insertiondate, s: deathdate },
      { f: insertiondate, s: exitdate },

      { f: birthdate, s: insertiondate2 },
      { f: insertiondate2, s: episoddate },
      { f: insertiondate2, s: deathdate },
      { f: insertiondate2, s: exitdate },

      { f: birthdate, s: removaldate },
      { f: entrydate, s: removaldate },
      { f: removaldate, s: deathdate },
      { f: removaldate, s: exitdate }
    )

    if (year >= 2020) {
      const diagdate = notes.dateField(note, bactf, bact.diagDate.v1)
      comparisons.push(
        { f: oldest, s: diagdate },
        { f: birthdate, s: diagdate },
        { f: episoddate, s: diagdate }
      )
    }

    if (year >= 2021) {
      const manipdate = notes.dateField(note, bactf, bact.previousManipDate)
      const enfmanipdate = notes.dateField(
        note, bactf, bact.enfPreviousManipDate
      )
      const beforeEpisod = common.beforeEpisod(
        episoddate.date, note[bact.episod.field]
      )

      comparisons.push(
        { f: birthdate, s: manipdate },
        { f: beforeEpisod, s: manipdate },
        { f: manipdate, s: episoddate },
        { f: insertiondate, s: manipdate },
        { f: manipdate, s: removaldate },
        { f: manipdate, s: deathdate },

        { f: birthdate, s: enfmanipdate },
        { f: beforeEpisod, s: enfmanipdate },
        { f: enfmanipdate, s: episoddate },
        { f: insertiondate2, s: enfmanipdate },
        { f: enfmanipdate, s: deathdate }
      )
    }
  }

  const sepsis = sheet[fields.sepsis]
  const sepsisNotes = result[fields.sepsis]
  assert.equals(sepsis.length, sepsisNotes.length)

  for (let i in sepsis) {
    const sep = sepsis[i]
    const note = sepsisNotes[i]

    const episoddate = notes.dateField(note, sep, bact.episod)

    comparisons.push(
      { f: start, s: episoddate },
      { f: episoddate, s: newest },
      { f: birthdate, s: episoddate },
      { f: episoddate, s: deathdate },
      { f: episoddate, s: exitdate }
    )

    if (v2) {
      const diagdate = notes.dateField(note, sep, bact.diagDate.v1)
      comparisons.push(
        { f: oldest, s: diagdate },
        { f: birthdate, s: diagdate },
        { f: episoddate, s: diagdate }
      )
    }
  }

  notes.compareDates(comparisons)
}

function buildNotes (result, sheet, year, surveillance, explaur) {
  checkDates(result, sheet, year)
  checkNumericValues(result, sheet)
  checkKts(result, sheet, year, explaur)
  checkBacteremias(result, sheet, explaur)
  common.checkUfs(result, sheet, 'neoInfADI', surveillance)
}

export default {
  build2019: function (result, sheet, surveillance) {
    buildNotes(result, sheet, 2019, surveillance)
    return result
  },

  build2020: function (result, sheet, surveillance) {
    buildNotes(result, sheet, 2020, surveillance)
    return result
  },

  build2021: function (result, sheet, surveillance) {
    buildNotes(
      result, sheet, 2021, surveillance,
      common.getExplaurUnit(sheet, surveillance)
    )
    return result
  },

  build2022: function (result, sheet, surveillance) {
    buildNotes(
      result, sheet, 2022, surveillance,
      common.getExplaurUnit(sheet, surveillance)
    )
    return result
  },

  build2023: function (result, sheet, surveillance) {
    buildNotes(
      result, sheet, 2023, surveillance,
      common.getExplaurUnit(sheet, surveillance)
    )
    return result
  },

  build2024: function (result, sheet, surveillance) {
    buildNotes(
      result, sheet, 2024, surveillance,
      common.getExplaurUnit(sheet, surveillance)
    )
    return result
  },

  build2025: function (result, sheet, surveillance) {
    buildNotes(
      result, sheet, 2025, surveillance,
      common.getExplaurUnit(sheet, surveillance)
    )
    return result
  },

  build2026: function (result, sheet, surveillance) {
    buildNotes(
      result, sheet, 2026, surveillance,
      common.getExplaurUnit(sheet, surveillance)
    )
    return result
  }
}
